/**
 * Help §5.8 — Reviewing & editing a document (`document/DocumentEditor.tsx`,
 * `document/DocumentView.tsx`).
 */
import { helpSectionMeta } from '../help-sections.js';
import {
  Btn,
  Bullets,
  HelpSectionCard,
  Li,
  P,
  SectionLink,
  Shot,
  Steps,
  SubHead,
} from '../HelpPrimitives.js';

export function Review(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('review')}>
      <P>
        Click <Btn>View / Edit</Btn> on a card to open the document. The header button toggles
        between <Btn>Edit</Btn> and <Btn>View</Btn>.
      </P>
      <Shot
        slug="document-modal"
        caption="The document window — the text in the middle, the actions along the header and footer."
      />

      <SubHead>Always read a document before approving it</SubHead>
      <Steps>
        <Li>
          Click <Btn>View / Edit</Btn> on the card to open it.
        </Li>
        <Li>Read it all the way through.</Li>
        <Li>
          For small wording changes, use <Btn>Edit</Btn> directly and <Btn>Save</Btn>.
        </Li>
        <Li>
          Happy with it? Click <Btn>Approve</Btn> — the card shows it as approved.
        </Li>
        <Li>
          Click <Btn>Close</Btn> to return to the project.
        </Li>
      </Steps>
      <P>
        Want a different version rather than small edits? Use <Btn>Regenerate</Btn> instead of
        editing line by line — see{' '}
        <SectionLink to="regenerate">Asking the AI to redo a document</SectionLink>.
      </P>

      <SubHead>What each button does</SubHead>
      <Bullets>
        <Li>
          <Btn>Save</Btn> — keep your changes.
        </Li>
        <Li>
          <Btn>Approve</Btn> — mark the document as ready.
        </Li>
        <Li>
          <Btn>Send</Btn> — where available, send it out.
        </Li>
        <Li>
          <Btn>Close</Btn> — go back to the cards.
        </Li>
      </Bullets>

      <SubHead>Formatting text in Edit mode</SubHead>
      <P>
        The text fields open in a proper rich-text editor with its own toolbar — not a plain box of
        markdown symbols. It gives you <Btn>Bold</Btn>, <Btn>Italic</Btn>, <Btn>Underline</Btn>,{' '}
        <Btn>Strikethrough</Btn>, headings, bullet and numbered lists, quotes, links and a text
        colour picker, plus a <Btn>Clear formatting</Btn> button if a paste brings in a mess. What
        you see in the editor is what the document actually looks like — no markdown syntax to
        remember or get wrong.
      </P>
    </HelpSectionCard>
  );
}
